import { useEffect, useState } from 'react'

interface Deposit {
  amount: string // Amount deposited in Wei
  priceAtDeposit: string // Price at the time of deposit
}

interface RewardsData {
  totalRewards: string // Total rewards in AVAX (as a string)
  deposits: Deposit[] // List of deposits
}

export function useRewards(
  userAddress: string | null,
  currentPrice: number | null,
  vaultBalance: BigInt | null,
) {
  const [rewards, setRewards] = useState<number>(0) // Total rewards in AVAX
  const [deposits, setDeposits] = useState<Deposit[]>([]) // User's deposit history
  const [loading, setLoading] = useState<boolean>(true) // Loading state
  const [error, setError] = useState<string | null>(null) // Error message, if any
  const [migrationAttempted, setMigrationAttempted] = useState<boolean>(false) // Track if migration has been attempted

  useEffect(() => {
    if (!userAddress || !currentPrice || vaultBalance === null) return

    async function fetchRewards() {
      try {
        setLoading(true)
        setError(null)

        // Fetch rewards
        const res = await fetch(
          `/api/rewards?userAddress=${userAddress}&currentPrice=${currentPrice}`,
        )
        if (!res.ok) throw new Error(`Failed to fetch rewards: ${res.statusText}`)

        const data: RewardsData = await res.json()

        // Trigger migration if no deposits and migration hasn't been attempted
        if (data.deposits.length === 0 && !migrationAttempted) {
          console.log('No deposits found, triggering migration...')
          setMigrationAttempted(true) // Mark migration as attempted
          await migrateUserDeposits(userAddress, vaultBalance)
          await fetchRewards() // Re-fetch rewards after migration
          return
        }

        // Parse the total rewards and deposits
        setRewards(parseFloat(data.totalRewards))
        setDeposits(data.deposits)
      } catch (err) {
        setError((err as Error).message)
      } finally {
        setLoading(false)
      }
    }

    async function migrateUserDeposits(userAddress: string, vaultBalance: BigInt) {
      try {
        const res = await fetch(
          `/api/migrate?userAddress=${userAddress}&vaultBalance=${vaultBalance}`,
          { method: 'GET' },
        )
        if (!res.ok) throw new Error(`Migration failed: ${res.statusText}`)
      } catch (err) {
        console.error('Error during migration:', err)
      }
    }

    // Fetch rewards initially
    fetchRewards()
  }, [userAddress, currentPrice, vaultBalance, migrationAttempted])

  return { rewards, deposits, loading, error }
}
